import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { DOMAINS, getDomainById } from '../../../shared/constants/domains';
import DomainBadge from '../../../shared/components/DomainBadge';
import SearchInput from '../../../shared/components/SearchInput';

/**
 * Browse/filter/search all flashcards with per-card stats.
 */
export default function CardBrowser({ cards }) {
  const [search, setSearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created'); // created, accuracy, domain, due

  const filtered = useMemo(() => {
    let result = [...cards];

    // Domain filter
    if (domainFilter !== 'all') {
      result = result.filter(c => c.domain === domainFilter);
    }

    // Search
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.front.toLowerCase().includes(q) ||
        c.back.toLowerCase().includes(q) ||
        c.tags.some(t => t.includes(q)) ||
        c.subdomain.toLowerCase().includes(q)
      );
    }

    // Sort
    result.sort((a, b) => {
      switch (sortBy) {
        case 'accuracy': {
          const aAcc = a.stats.times_seen > 0 ? a.stats.times_correct / a.stats.times_seen : -1;
          const bAcc = b.stats.times_seen > 0 ? b.stats.times_correct / b.stats.times_seen : -1;
          return aAcc - bAcc; // Worst first
        }
        case 'domain':
          return a.domain.localeCompare(b.domain);
        case 'due':
          return (a.sr_data.next_review_date || '').localeCompare(b.sr_data.next_review_date || '');
        default: // created
          return new Date(b.created_at) - new Date(a.created_at);
      }
    });

    return result;
  }, [cards, search, domainFilter, sortBy]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 page-enter">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Browse Cards</h2>
        <Link
          to="/flashcards/edit"
          className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg
            hover:from-blue-500 hover:to-blue-400 text-sm font-medium transition-all duration-200"
        >
          + Add Card
        </Link>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 mb-4">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search cards..."
          className="flex-1"
        />
        <select
          value={domainFilter}
          onChange={e => setDomainFilter(e.target.value)}
          className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white text-sm
            focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-colors"
        >
          <option value="all">All Domains</option>
          {DOMAINS.map(d => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-600 text-white text-sm
            focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-colors"
        >
          <option value="created">Newest First</option>
          <option value="accuracy">Weakest First</option>
          <option value="domain">By Domain</option>
          <option value="due">By Due Date</option>
        </select>
      </div>

      <p className="text-sm text-gray-500 mb-4">{filtered.length} cards</p>

      {/* Card list */}
      <div className="space-y-2.5">
        {filtered.slice(0, 100).map(card => {
          const acc = card.stats.times_seen > 0
            ? Math.round((card.stats.times_correct / card.stats.times_seen) * 100)
            : null;
          const domain = getDomainById(card.domain);
          const domainColor = domain?.color || '#4A90D9';

          return (
            <Link
              key={card.id}
              to={`/flashcards/edit/${card.id}`}
              className="block bg-gray-800/80 border border-gray-700/50 rounded-xl p-4
                hover:bg-gray-750 hover:border-gray-600 transition-all duration-200
                hover:translate-x-1"
              style={{ borderLeftWidth: '3px', borderLeftColor: domainColor }}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate font-medium">{card.front}</p>
                  <p className="text-gray-500 text-xs mt-1.5 truncate">{card.back}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {acc !== null && (
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      acc >= 80 ? 'text-green-400 bg-green-400/10' :
                      acc >= 50 ? 'text-yellow-400 bg-yellow-400/10' :
                      'text-red-400 bg-red-400/10'
                    }`}>
                      {acc}%
                    </span>
                  )}
                  <DomainBadge domainId={card.domain} size="xs" />
                </div>
              </div>
              {card.tags.length > 0 && (
                <div className="flex gap-1.5 mt-2.5">
                  {card.tags.slice(0, 5).map(tag => (
                    <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-gray-700/60 text-gray-400">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          );
        })}
        {filtered.length > 100 && (
          <p className="text-center text-gray-500 text-sm py-4">
            Showing first 100 of {filtered.length} cards
          </p>
        )}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-16">
          <div className="text-4xl mb-3 opacity-50">{'\u{1F50D}'}</div>
          <p className="text-gray-500">No cards match your filters.</p>
        </div>
      )}
    </div>
  );
}
